import React, { memo, ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { CustomModal, Flex, UiButton, UiText } from '@shared/ui';
import { Loader } from '@shared/ui/CustomModal/ui/Loader/Loader';

import cls from './AddProject.module.css';
import { AddProjectContext } from './Context/AddProjectContext';
import { FirstStep } from './FirstStep/FirstStep';
import { SecondStep } from './SecondStep/SecondStep';
import { useAddProject } from './useAddProject';

interface AddProjectProps {
    classes?: string;
    iShowCreate: boolean;
}

export const AddProject = memo(({ classes, iShowCreate = false }: AddProjectProps) => {
    const { t } = useTranslation();

    const {
        isOpen,
        onHideModal,
        onStepChange,
        step,
        onLoaderChange,
        isLoader,
        form,
        formErrors,
        onFormChange,
        contextValue,
    } = useAddProject();

    const stepMapping: Record<number, ReactNode> = {
        1: (
            <FirstStep
                onLoaderChange={onLoaderChange}
                form={form}
                formErrors={formErrors}
                onFormChange={onFormChange}
            />
        ),
        2: <SecondStep onLoaderChange={onLoaderChange} />,
    };

    return (
        <AddProjectContext.Provider value={contextValue}>
            {isOpen && (
                <CustomModal
                    contentClasses={cls.ProjectTeam}
                    isOpen={isOpen}
                    onHideModal={onHideModal}
                >
                    {isLoader && <Loader variant="light" />}
                    <CustomModal.Header>
                        <Flex direction="column" align="start" gap={10}>
                            {step === 1
                                ? t('AddProject.return.Modal.ModalHeader.content.create_object')
                                : t('AddProject.return.Modal.ModalHeader.content.team_object')}

                            {step === 2 && (
                                <div className={cls.subtitle}>
                                    <UiText variant="mediumPlain">
                                        {t('AddProject.return.Modal.ModalHeader.subtitle')}
                                    </UiText>
                                </div>
                            )}
                        </Flex>
                    </CustomModal.Header>
                    <CustomModal.Body>{stepMapping[step]}</CustomModal.Body>
                    <CustomModal.Footer>
                        <UiButton
                            onClick={onStepChange}
                            btnText={
                                step === 1
                                    ? t('AddProject.return.Modal.ModalFooter.Button.continue')
                                    : t('AddProject.return.Modal.ModalFooter.Button.done')
                            }
                        />
                    </CustomModal.Footer>
                </CustomModal>
            )}
        </AddProjectContext.Provider>
    );
});
